import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface BusinessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  name: string
  email: string
  phone: string
  website: string
  timezone: string
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onPhoneChange: (value: string) => void
  onWebsiteChange: (value: string) => void
  onTimezoneChange: (value: string) => void
  onSave: () => void
  isPending: boolean
  isValid: boolean
}

export function BusinessDialog({
  open,
  onOpenChange,
  name,
  email,
  phone,
  website,
  timezone,
  onNameChange,
  onEmailChange,
  onPhoneChange,
  onWebsiteChange,
  onTimezoneChange,
  onSave,
  isPending,
  isValid,
}: BusinessDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (isValid) onSave()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('settings.dialogs.business.title')}</DialogTitle>
            <DialogDescription>{t('settings.dialogs.business.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="settings-business-name">
                {t('settings.dialogs.business.displayName')}
              </label>
              <Input
                id="settings-business-name"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="settings-business-email">
                {t('settings.dialogs.business.contactEmail')}
              </label>
              <Input
                id="settings-business-email"
                type="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="settings-business-phone">
                {t('settings.dialogs.business.contactPhone')}
              </label>
              <Input
                id="settings-business-phone"
                value={phone}
                onChange={(e) => onPhoneChange(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="settings-business-website">
                {t('settings.dialogs.business.website')}
              </label>
              <Input
                id="settings-business-website"
                value={website}
                onChange={(e) => onWebsiteChange(e.target.value)}
                placeholder="https://"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label className="text-sm font-medium" htmlFor="settings-business-timezone">
                {t('settings.dialogs.business.timezone')}
              </label>
              <Input
                id="settings-business-timezone"
                value={timezone}
                onChange={(e) => onTimezoneChange(e.target.value)}
                placeholder={t('settings.dialogs.business.timezonePlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !isValid}>
              {isPending ? t('settings.dialogs.business.saving') : t('settings.dialogs.business.saveBusiness')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
